"use client";

import { useCallback } from "react";

import { useDashboard } from "./use-dashboard";
import { useSaveDashboardLayout } from "./use-dashboard-prefs";

/**
 * Read and write ONE widget's params, which live in the user's saved layout.
 *
 * A widget's settings are per user and persist across devices, so they belong in
 * the layout rather than in component state or localStorage — the same place the
 * order and the hidden set already live, saved through the same endpoint.
 *
 * The whole layout is sent, not a patch, because that is the contract: the
 * endpoint replaces the layout wholesale. So this merges into the CURRENT
 * catalog response — dropping `order` or another widget's params from the body
 * would delete them.
 *
 * Nothing here invalidates the widget's own data. It doesn't have to: saving
 * refreshes the catalog, the catalog carries the new params, and the params are
 * part of the widget's query key — so the card refetches itself because it is
 * asking a different question, not because anyone told it to.
 */
export function useWidgetParams(widgetKey: string) {
  const { data } = useDashboard();
  const save = useSaveDashboardLayout();

  const layout = data?.layout;
  const params = layout?.params?.[widgetKey];

  // Awaitable, not fire-and-forget: the caller is a dialog with an Apply
  // button, and it can only close on success — and leave itself open with the
  // user's edits intact on failure — if it can tell the two apart.
  const setParams = useCallback(
    async (changes: Record<string, unknown>) => {
      // No layout yet means the catalog hasn't landed, which means no control
      // built on this is on screen to call it.
      if (!layout) return;

      await save.mutateAsync({
        ...layout,
        params: {
          ...layout.params,
          // Merged, so a control that owns one key can't wipe a sibling's.
          [widgetKey]: { ...params, ...changes },
        },
      });
    },
    [layout, params, save, widgetKey],
  );

  return { params, setParams, isSaving: save.isPending };
}
